import { DeleteOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Tag } from "antd";
import { FC } from "react";

import { SortingDirection } from "@app/shared/lib/enums/sorting-direction";
import { useLoc, useSdk } from "@app/shared/lib/hooks";
import { DateTime, Loc, Price, Table, TruncatedText } from "@app/shared/ui";
import Request from "@app/shared/ui/Request";

export const TransactionRequestTable: FC = () => {
  const loc = useLoc();
  const sdk = useSdk();

  return (
    <Table
      defaultFilters={{ take: 25 }}
      fetchItemsByFilter={async (filters) => {
        const res = await sdk.partnership.requestStore.fetch({
          ...filters,
          sortType: filters.sortType === SortingDirection.asc ? "Asc" : "Desc",
        });

        return {
          items: res.body.items,
          filters: {
            ...res.body.filter,
            sortType:
              res.body.filter.sortType === "Desc"
                ? SortingDirection.desc
                : SortingDirection.asc,
            pageTotal: sdk.partnership.requestStore.filter.pageTotal,
            page: sdk.partnership.requestStore.filter.page,
          },
        };
      }}
      scroll={{ x: "max-content" }}
      columns={[
        {
          title: loc.word("Global.createdDate", { default: "Created date" }),
          sorter: true,
          dataIndex: "CreatedDate",
          width: "200px",
          render: (_text, item) => {
            return <DateTime date={item.createdDate} />;
          },
        },
        {
          title: loc.word("Partnership.request.amount", { default: "Amount" }),
          dataIndex: "Amount",
          sorter: true,
          align: "right",
          width: "150px",
          render: (_text, item) => {
            return <Price price={item.amount} />;
          },
        },
        {
          title: loc.word("Global.comment", { default: "Comment" }),
          width: "250px",
          render: (_text, item) => {
            if (!item.adminComment && !item.partnerComment) return "-";

            return (
              <div style={{ maxWidth: 250 }} className="fs-12">
                {item.adminComment && (
                  <div className="flex gap-5 align-center">
                    <Loc word="Global.admin" className="color-secondary" />
                    <TruncatedText maxLines={3}>
                      {item.adminComment}
                    </TruncatedText>
                  </div>
                )}
                {item.partnerComment && (
                  <div className="flex gap-5 align-center">
                    <Loc
                      word="Partnership.partner"
                      className="color-secondary"
                    />
                    <TruncatedText maxLines={3}>
                      {item.partnerComment}
                    </TruncatedText>
                  </div>
                )}
              </div>
            );
          },
        },
        {
          title: loc.word("Global.status", { default: "Status" }),
          dataIndex: "Status",
          sorter: true,
          width: "150px",
          render: (_text, item) => {
            switch (item.status) {
              case "approved":
                return (
                  <Tag color="success">
                    <Loc
                      word="Partnership.request.approved"
                      default="Approved"
                    />
                  </Tag>
                );
              case "declined":
                return (
                  <Tag color="error">
                    <Loc
                      word="Partnership.request.declined"
                      default="Declined"
                    />
                  </Tag>
                );
              default:
                return (
                  <Tag color="warning">
                    <Loc
                      word="Partnership.request.requested"
                      default="Requested"
                    />
                  </Tag>
                );
            }
          },
        },
        {
          title: loc.word("Global.actions", { default: "Actions" }),
          render: (_text, item) => {
            if (item.status === "requested")
              return (
                <Request
                  action={() => sdk.partnership.requestStore.delete(item.id)}
                >
                  {({ exec }) => (
                    <Popconfirm
                      title={
                        <Loc
                          word="Partnership.request.deleteConfirm"
                          default="Are you sure you want to delete this request?"
                        />
                      }
                      onConfirm={exec}
                    >
                      <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </Request>
              );

            return null;
          },
        },
      ]}
      rowBordered={false}
      className="partnership-table"
      size="small"
      style={{ maxWidth: 1280 }}
    />
  );
};
